import React, { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import designTokens from '@/design-tokens';
import { Info } from '../Icons/Info';
import { useReducedMotion } from '@/hooks/useReducedMotion';

/**
 * `settled` is `general`'s ground with a disabled label — a record that has
 * concluded and no longer asks anything of the reader.
 *
 * ⚠️ Distinct from `final`, deliberately. `final` is the dark storm chip and
 * stays that way: a committed sales order, the owner badge and the MO modals all
 * want that prominence. An ENDED recurring order does not — Percy, session 232:
 * *"'final' color is too prominent"* on a row that is simply over.
 */
export type ChipStatus = 'success' | 'interesting' | 'info' | 'warning' | 'error' | 'general' | 'final' | 'settled';
export type IconPosition = 'left' | 'right';
export type ChipSize = 'default' | 'small';

interface ChipProps {
  children: React.ReactNode;
  status?: ChipStatus;
  icon?: React.ReactNode;
  showIcon?: boolean;
  iconPosition?: IconPosition;
  size?: ChipSize;
  className?: string;
  /**
   * Opt-in: the label shown while the chip is hovered (or tapped), e.g. the dashboard tiles' `+$412` behind `+12%`
   * (Percy, 2026-10-14). The chip's width animates between the two labels. Absent, the chip renders exactly as before.
   */
  hoverLabel?: React.ReactNode;
}

const SWAP_MS = 200;

/**
 * The two labels stacked, the visible one switched INSTANTLY, inside a box whose width animates from one label's
 * measured width to the other's — CSS cannot transition `width: auto`, so both are measured.
 * ⚠️ No opacity crossfade: two half-visible labels on top of each other, and text on its own compositing layer, both
 * read as a heavier weight (Percy, 2026-10-14). ⚠️ A BLOCK of one line-height, not `inline-block`: an inline-block
 * whose text is all absolutely positioned sits on the line by its bottom edge, which pushed the label off-centre.
 */
const SwapLabel: React.FC<{ idle: React.ReactNode; hover: React.ReactNode; hovered: boolean; lineHeight: string }> = ({
  idle,
  hover,
  hovered,
  lineHeight,
}) => {
  const reduced = useReducedMotion();
  const idleRef = useRef<HTMLSpanElement>(null);
  const hoverRef = useRef<HTMLSpanElement>(null);
  const [widths, setWidths] = useState<{ idle: number; hover: number } | null>(null);
  useLayoutEffect(() => {
    const measure = () =>
      setWidths({ idle: idleRef.current?.offsetWidth ?? 0, hover: hoverRef.current?.offsetWidth ?? 0 });
    measure();
    // A web font landing after first paint changes both widths
    document.fonts?.ready.then(measure).catch(() => {});
  }, [idle, hover]);
  const layer = (shown: boolean): React.CSSProperties => ({
    position: 'absolute',
    left: 0,
    top: 0,
    whiteSpace: 'nowrap',
    visibility: shown ? 'visible' : 'hidden',
  });
  // Unmeasured (jsdom, first frame): size to the label showing
  const width = widths && widths.idle > 0 ? (hovered ? widths.hover : widths.idle) : undefined;
  return (
    <span
      data-hovered={hovered || undefined}
      style={{
        position: 'relative',
        display: 'block',
        height: lineHeight,
        width,
        transition: reduced ? 'none' : `width ${SWAP_MS}ms ease-out`,
      }}
    >
      {/* In flow only while unmeasured, so the box has a size before the first measure */}
      {!width && <span style={{ visibility: 'hidden', whiteSpace: 'nowrap' }}>{hovered ? hover : idle}</span>}
      <span ref={idleRef} data-testid="chip-label" style={layer(!hovered)} aria-hidden={hovered || undefined}>
        {idle}
      </span>
      <span ref={hoverRef} data-testid="chip-hover-label" style={layer(hovered)} aria-hidden={!hovered || undefined}>
        {hover}
      </span>
    </span>
  );
};

export const Chip: React.FC<ChipProps> = ({
  children,
  status = 'general',
  icon,
  showIcon = false,
  iconPosition = 'left',
  size = 'default',
  className,
  hoverLabel,
}) => {
  const [hovered, setHovered] = useState(false);
  const swaps = hoverLabel !== undefined && hoverLabel !== null;
  // Get colors based on status
  const getStatusColors = () => {
    switch (status) {
      case 'success':
        return {
          backgroundColor: designTokens['surface/feedback/success'] || '#d3f9d8',
          textColor: designTokens['surface/feedback/success-heavy'] || '#2b8a3e',
          iconColor: designTokens['surface/feedback/success-heavy'] || '#2b8a3e'
        };
      case 'interesting':
        return {
          backgroundColor: designTokens['surface/feedback/interesting'] || '#e6d5fd',
          textColor: designTokens['surface/feedback/interesting-heavy'] || '#984dfa',
          iconColor: designTokens['surface/feedback/interesting-heavy'] || '#984dfa'
        };
      case 'info':
        return {
          backgroundColor: designTokens['surface/feedback/info'] || '#e3f6ff',
          textColor: designTokens['surface/feedback/info-heavy'] || '#028afc',
          iconColor: designTokens['surface/feedback/info-heavy'] || '#028afc'
        };
      case 'warning':
        return {
          backgroundColor: designTokens['surface/feedback/warning'] || '#fff9de',
          textColor: designTokens['surface/feedback/warning-heavy'] || '#ff9b43',
          iconColor: designTokens['surface/feedback/warning-heavy'] || '#ff9b43'
        };
      case 'error':
        return {
          backgroundColor: designTokens['surface/feedback/error'] || '#ffe3e3',
          textColor: designTokens['surface/feedback/error-heavy'] || '#c92a2a',
          iconColor: designTokens['surface/feedback/error-heavy'] || '#c92a2a'
        };
      case 'general':
        return {
          backgroundColor: designTokens['surface/greyscale/mist'] || '#f1f3f5',
          textColor: designTokens['text/primary'] || '#343a40',
          iconColor: designTokens['icon/primary'] || '#343a40'
        };
      case 'settled':
        return {
          backgroundColor: designTokens['surface/greyscale/mist'] || '#f1f3f5',
          textColor: designTokens['text/disabled'] || '#adb5bd',
          iconColor: designTokens['text/disabled'] || '#adb5bd'
        };
      case 'final':
        return {
          backgroundColor: designTokens['surface/greyscale/storm'] || '#495057',
          textColor: designTokens['text/inverse'] || '#ffffff',
          iconColor: designTokens['icon/inverse'] || '#ffffff'
        };
      default:
        return {
          backgroundColor: designTokens['surface/greyscale/mist'] || '#e9ecef',
          textColor: designTokens['text/primary'] || '#343a40',
          iconColor: designTokens['icon/primary'] || '#343a40'
        };
    }
  };

  const colors = getStatusColors();

  // Default icon component if showIcon is true but no icon provided
  const defaultIcon = (
    <Info
      fillColor={colors.iconColor}
      strokeColor={colors.iconColor}
    />
  );

  const renderIcon = showIcon ? (icon || defaultIcon) : null;

  // Size-based dimensions
  const getSizeStyles = () => {
    if (size === 'small') {
      return {
        height: '20px',
        fontSize: designTokens['font/size/xxs'] || '10px',
        lineHeight: '14px',
        iconSize: '12px',
        gap: '4px',
        padding: '4px'
      };
    }
    return {
      height: '24px',
      fontSize: designTokens['font/size/xs'] || '12px',
      lineHeight: '16px',
      iconSize: '16px',
      gap: '4px',
      padding: '6px'
    };
  };

  const sizeStyles = getSizeStyles();

  // Container styles
  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: sizeStyles.height,
    paddingLeft: sizeStyles.padding,
    paddingRight: sizeStyles.padding,
    gap: sizeStyles.gap,
    backgroundColor: colors.backgroundColor,
    borderRadius: designTokens['radius/md'] || '6px',
  };

  // Text styles
  const textStyle: React.CSSProperties = {
    fontFamily: designTokens['font/family/medium'] || 'Montserrat Medium, system-ui, sans-serif',
    fontSize: sizeStyles.fontSize,
    lineHeight: sizeStyles.lineHeight,
    color: colors.textColor,
    margin: 0,
  };

  // Icon container styles
  const iconStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: sizeStyles.iconSize,
    height: sizeStyles.iconSize,
    color: colors.iconColor,
    flexShrink: 0,
  };

  return (
    <div 
      className={cn(className)}
      style={containerStyle}
      onMouseEnter={swaps ? () => setHovered(true) : undefined}
      onMouseLeave={swaps ? () => setHovered(false) : undefined}
    >
      {renderIcon && iconPosition === 'left' && (
        <span style={iconStyle}>
          {renderIcon}
        </span>
      )}
      
      <span style={textStyle}>
        {swaps ? <SwapLabel idle={children} hover={hoverLabel} hovered={hovered} lineHeight={sizeStyles.lineHeight} /> : children}
      </span>
      
      {renderIcon && iconPosition === 'right' && (
        <span style={iconStyle}>
          {renderIcon}
        </span>
      )}
    </div>
  );
};